import { useEffect, useState } from 'react';

// 12-hour time picker — Hour <select>, Minute text input (any 0-59),
// AM/PM <select>. We avoid <input type="time"> because the native
// picker silently drops the value on mobile Safari and Samsung
// Internet when dismissed by swipe instead of "Done".
//
// Minute is a free-form text input (numeric keypad on mobile) so hosts
// can pick any minute, e.g. 7:42 or 6:08, not just 5-minute increments.
//
// `value` / `onChange` use the canonical 24-hour "HH:MM" string the
// backend already stores. Internal local state retains the partial
// selection so picking just one field doesn't reset the others.

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);

function parse24(str) {
  if (!str || typeof str !== 'string' || !str.includes(':')) return { h12: '', m: '', ap: '' };
  const [hStr, mStr] = str.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return { h12: '', m: '', ap: '' };
  const ap = h >= 12 ? 'PM' : 'AM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return { h12: String(h12), m: String(m).padStart(2, '0'), ap };
}

function format24(h12, m, ap) {
  if (!h12 || !m || !ap) return '';
  let h = parseInt(h12, 10);
  const mNum = parseInt(m, 10);
  if (Number.isNaN(h) || Number.isNaN(mNum) || mNum < 0 || mNum > 59) return '';
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(mNum).padStart(2, '0')}`;
}

// Strip non-digits and clamp 0-59. Returns '' for empty input so users
// can clear the field.
function sanitizeMinuteInput(raw) {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 2);
  if (!digits) return '';
  const n = parseInt(digits, 10);
  if (Number.isNaN(n)) return '';
  return String(Math.min(59, Math.max(0, n)));
}

export default function TimePicker12h({ value, onChange, testId }) {
  const [h12, setH12] = useState(() => parse24(value).h12);
  const [m, setM] = useState(() => parse24(value).m);
  const [ap, setAp] = useState(() => parse24(value).ap);

  // Sync down when the parent feeds us a fresh canonical value (edit
  // modal opens for a different event, form reset, etc.). Compare
  // against our own format so we don't clobber a partial selection.
  useEffect(() => {
    const incoming = parse24(value);
    const myFormatted = format24(h12, m, ap);
    if (value !== myFormatted) {
      setH12(incoming.h12);
      setM(incoming.m);
      setAp(incoming.ap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (nh, nm, nap) => {
    setH12(nh);
    setM(nm);
    setAp(nap);
    if (nh && nm !== '' && nap) {
      onChange(format24(nh, nm, nap));
    } else {
      onChange('');
    }
  };

  const onMinuteChange = (raw) => {
    commit(h12, sanitizeMinuteInput(raw), ap);
  };

  const onMinuteBlur = () => {
    if (m === '') return;
    // Pad to two digits on blur so "7" displays as "07" — keeps the
    // form looking tidy even though "7" is already a valid minute.
    const padded = String(parseInt(m, 10)).padStart(2, '0');
    if (padded !== m) commit(h12, padded, ap);
  };

  const baseSelect = "h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#56c6df]";
  const minuteInput = "h-10 w-16 rounded-xl border border-slate-200 bg-white px-3 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#56c6df]";

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <select
        value={h12}
        onChange={e => commit(e.target.value, m, ap)}
        className={baseSelect}
        data-testid={testId ? `${testId}-hour` : undefined}
        aria-label="Hour"
      >
        <option value="">Hr</option>
        {HOURS.map(h => <option key={h} value={String(h)}>{h}</option>)}
      </select>
      <span className="text-slate-400">:</span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        placeholder="MM"
        value={m}
        onChange={e => onMinuteChange(e.target.value)}
        onBlur={onMinuteBlur}
        className={minuteInput}
        data-testid={testId ? `${testId}-min` : undefined}
        aria-label="Minute"
      />
      <select
        value={ap}
        onChange={e => commit(h12, m, e.target.value)}
        className={baseSelect}
        data-testid={testId ? `${testId}-ampm` : undefined}
        aria-label="AM/PM"
      >
        <option value="">--</option>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
